import { Input, Typography } from '@breetometer/components';
import { capitalizeFirstLetter } from 'BreetHelpers';
import {
	type ChangeEvent,
	type ClipboardEvent,
	forwardRef,
	type KeyboardEvent,
	useCallback,
	useImperativeHandle,
	useRef,
	useState,
} from 'react';

import type { IOTPInput } from '../helpers';

export const OtpInput = forwardRef(
	({ onChange, error, otpLength, position = 'center', id, showError, maskInputValue }: IOTPInput, ref) => {
		const [otp, setOtp] = useState<string[]>(new Array(otpLength).fill(''));
		const [visibleIndexes, setVisibleIndexes] = useState<boolean[]>(new Array(otpLength).fill(false));

		const inputRef = useRef<HTMLInputElement>(null);

		const otpIdFragment = id ? `${id}-` : '';

		const otpId = `otp-input-${otpIdFragment}`;

		useImperativeHandle(ref, () => ({
			reset: () => {
				setOtp(new Array(otpLength).fill(''));
				setVisibleIndexes(new Array(otpLength).fill(false));
			},
			focusFirst: () => document.getElementById(`${otpId}0`)?.focus(),
		}));

		const toggleVisibilityAfterDelay = useCallback((index: number) => {
			setTimeout(() => {
				setVisibleIndexes((prev) => {
					const updated = [...prev];
					updated[index] = false;
					return updated;
				});
			}, 400);
		}, []);

		const handleChange = useCallback(
			(index: number) => (e: ChangeEvent<HTMLInputElement>) => {
				const { value } = e.target;

				if (!/^\d?$/.test(value)) return;

				const newOtp = [...otp];
				newOtp[index] = value;
				setOtp(newOtp);

				if (value && maskInputValue) {
					const newVisible = [...visibleIndexes];
					newVisible[index] = true;
					setVisibleIndexes(newVisible);

					toggleVisibilityAfterDelay(index);
				}

				const otpString = newOtp.join('');
				if (otpString.length === otpLength) {
					inputRef.current?.blur();
					onChange?.(otpString);
				}

				if (value && index < otpLength - 1) {
					const nextInput = document.getElementById(`${otpId}${index + 1}`);
					nextInput?.focus();
				}
			},
			[onChange, otp, otpId, otpLength, toggleVisibilityAfterDelay, visibleIndexes, maskInputValue]
		);

		const handleBackspace = useCallback(
			(index: number) => (e: KeyboardEvent<HTMLInputElement>) => {
				if (e.key === 'Backspace') {
					if (error) {
						setOtp(new Array(otpLength).fill(''));
						setVisibleIndexes(new Array(otpLength).fill(false));
						document.getElementById(`${otpId}0`)?.focus();
						onChange?.('');
						return;
					}

					if (!otp[index] && index > 0) {
						const prevInput = document.getElementById(`${otpId}${index - 1}`);
						prevInput?.focus();
					}
				}
			},
			[error, onChange, otp, otpId, otpLength]
		);

		const handlePaste = useCallback(
			(e: ClipboardEvent<HTMLInputElement>) => {
				const pasteData = e.clipboardData.getData('text');
				const regex = new RegExp(`^\\d{${otpLength}}$`);

				if (regex.test(pasteData)) {
					const newOtp = pasteData.split('');
					setOtp(newOtp);
					setVisibleIndexes(new Array(otpLength).fill(false));
					onChange?.(pasteData);

					const lastInput = document.getElementById(`${otpId}${otpLength - 1}`);
					lastInput?.focus();
				}
				e.preventDefault();
			},
			[onChange, otpId, otpLength]
		);

		const inputClass = (indexArg: number) =>
			`${maskInputValue && !visibleIndexes[indexArg] ? 'otp-hidden' : ''}${error ? ' error' : ''}`;

		const otpClass = `otp_inputsection_content ${position}`;

		return (
			<div
				className='otp_inputsection'
				data-testid='otp-input'
			>
				<div className={otpClass}>
					{otp.map((otpItem, index) => (
						<Input
							key={String(index + otpLength)}
							id={`${otpId}${index}`}
							label=''
							type='text'
							inputMode='numeric'
							pattern='[0-9]*'
							value={otpItem}
							onChange={handleChange(index)}
							onKeyDown={handleBackspace(index)}
							onPaste={handlePaste}
							className={inputClass(index)}
							maxLength={1}
							autoComplete='one-time-code'
							ref={inputRef}
						/>
					))}
				</div>
				{showError && <Typography className='bt_input-error-message'>{capitalizeFirstLetter(error)}</Typography>}
			</div>
		);
	}
);

OtpInput.displayName = 'OtpInput';
